import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box, Button, Typography, TextField, InputAdornment, Avatar, Chip,
  IconButton, Tooltip, CircularProgress, Grid, Skeleton, alpha,
} from "@mui/material";
import { useDeleteVendorMutation, useGetVendorQuery } from "../../../Api/attoDeskApi";
import { IVendor } from "../../../Api/Interface/api.interface";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import ActionButtons from "../../../Components/ui/ActionButtons";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { NAVY, NAVY_DARK, GOLD, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY } from "../../../theme/tokens";

const LARAVEL_API_URL = (process.env.REACT_APP_LARAVEL_API || "").trim();

const SupplierList: React.FC = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [deleteTarget, setDeleteTarget] = useState<IVendor | null>(null);

  const { data, isLoading, refetch } = useGetVendorQuery({ searchText: debouncedSearch, id: "" });
  const [deleteVendor] = useDeleteVendorMutation();

  const suppliers: IVendor[] = useMemo(() => {
    return Array.isArray(data?.data) ? (data?.data as IVendor[]) : [];
  }, [data]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await deleteVendor(deleteTarget.id.toString()).unwrap();
      if (res.status) { showMessage("Supplier deleted"); setDeleteTarget(null); }
      else showErrorMessage("Failed to delete");
    } catch { showErrorMessage("Failed to delete"); }
  };

  const getInitials = (s: IVendor) => {
    if (s.customerCompanyName) return s.customerCompanyName.charAt(0).toUpperCase();
    return ((s.customerFirstName?.charAt(0) || "") + (s.customerLastName?.charAt(0) || "")).toUpperCase() || "S";
  };

  const getName = (s: IVendor) => s.customerCompanyName || `${s.customerFirstName || ""} ${s.customerLastName || ""}`.trim() || "Unnamed";
  const getLocation = (s: IVendor) => [s.customerCity, s.customerProvince].filter(Boolean).join(", ");

  if (isLoading) return (
    <Box sx={{ p: 3, bgcolor: "#F8FAFC", minHeight: "100vh" }}>
      {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={80} sx={{ mb: 2, borderRadius: "16px" }} />)}
    </Box>
  );

  return (
    <Box sx={{ p: 3, bgcolor: "#F8FAFC", minHeight: "100vh" }}>
      {/* ═══ HEADER ═══ */}
      <Box sx={{
        bgcolor: "#fff", borderRadius: "16px", border: `1px solid ${BORDER}`,
        boxShadow: "0 6px 20px rgba(0,0,0,0.06)", px: 3, py: 2.5, mb: 3,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, flexWrap: "wrap" }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
            boxShadow: `0 6px 18px ${alpha(NAVY, 0.35)}`,
            border: `1px solid ${alpha(GOLD, 0.35)}`,
          }}>
            <StorefrontIcon sx={{ fontSize: 22, color: GOLD }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography fontSize={22} fontWeight={800} color={NAVY} letterSpacing="-0.02em">Suppliers</Typography>
            <Typography fontSize={12} color={TEXT_SECONDARY}>{suppliers.length} suppliers · vendors and product sources</Typography>
          </Box>
          <TextField size="small" placeholder="Search suppliers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: TEXT_SECONDARY }} /></InputAdornment> }}
            sx={{
              width: 280,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5, fontSize: 13, bgcolor: "#fff",
                "& fieldset": { borderColor: BORDER },
                "&:hover fieldset": { borderColor: alpha(GOLD, 0.6) },
                "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: 1.5 },
              },
            }} />
          <Button
            variant="contained" color="secondary" disableElevation
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate("/vendor/create")}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 12, px: 2.5,
              color: NAVY_DARK,
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
              boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
              "&:hover": {
                background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)`,
                boxShadow: `0 6px 20px ${alpha(GOLD, 0.55)}`,
                transform: "translateY(-1px)",
              },
            }}>
            Add Supplier
          </Button>
        </Box>
      </Box>

      {/* ═══ STAT CARDS ═══ */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total Suppliers", value: suppliers.length, icon: <StorefrontIcon />, color: NAVY, bg: alpha(NAVY, 0.08) },
          { label: "With Phone", value: suppliers.filter(s => s.customerMobileNum || s.customerCompanyPhoneNumebr).length, icon: <PhoneIcon />, color: "#2563EB", bg: "#EFF6FF" },
          { label: "With Location", value: suppliers.filter(s => s.customerCity).length, icon: <LocationOnIcon />, color: "#7C3AED", bg: "#FAF5FF" },
        ].map((c, i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Box sx={{
              bgcolor: "#fff", borderRadius: "16px", border: `1px solid ${BORDER}`, p: 2.5,
              display: "flex", alignItems: "center", gap: 2,
              boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
              transition: "all 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 22px rgba(0,0,0,0.08)", borderColor: alpha(GOLD, 0.4) },
            }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: c.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {React.cloneElement(c.icon, { sx: { fontSize: 22, color: c.color } })}
              </Box>
              <Box>
                <Typography fontSize={10} color={TEXT_SECONDARY} fontWeight={700} textTransform="uppercase" letterSpacing="0.06em">{c.label}</Typography>
                <Typography fontSize={24} fontWeight={800} color={NAVY}>{c.value}</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* ═══ SUPPLIER LIST ═══ */}
      {suppliers.length === 0 ? (
        <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: `1px solid ${BORDER}`, boxShadow: "0 6px 20px rgba(0,0,0,0.06)", textAlign: "center", py: 10 }}>
          <StorefrontIcon sx={{ fontSize: 48, color: "#D1D5DB", mb: 2 }} />
          <Typography fontSize={16} fontWeight={700} color={TEXT_PRIMARY} mb={0.5}>No suppliers yet</Typography>
          <Typography fontSize={13} color={TEXT_SECONDARY} mb={3}>Add your first supplier to get started</Typography>
          <Button
            variant="contained" color="secondary" disableElevation
            startIcon={<AddIcon />}
            onClick={() => navigate("/vendor/create")}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 13, px: 3,
              color: NAVY_DARK,
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
              boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
              "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
            }}>
            Add Supplier
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {suppliers.map((s) => {
            const name = getName(s);
            const phone = s.customerMobileNum || s.customerCompanyPhoneNumebr || "";
            const email = s.customerEmail || s.customerCompanyEmail || "";
            const location = getLocation(s);

            return (
              <Box key={s.id} sx={{
                bgcolor: "#fff", borderRadius: "16px", border: `1px solid ${BORDER}`, p: 2.5,
                display: "flex", alignItems: "center", gap: 2.5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: alpha(GOLD, 0.4),
                  boxShadow: "0 8px 22px rgba(0,0,0,0.08)",
                  transform: "translateY(-2px)",
                },
              }}>
                {/* Avatar — renders customerImg when available, otherwise falls back
                    to initials. MUI Avatar auto-falls-back when src is empty/broken. */}
                <Avatar
                  variant="rounded"
                  src={s.customerImg ? `${LARAVEL_API_URL}/${s.customerImg}` : undefined}
                  alt={getName(s)}
                  sx={{
                    width: 48, height: 48, borderRadius: 2.5,
                    bgcolor: alpha(NAVY, 0.06),
                    color: NAVY,
                    fontSize: 18, fontWeight: 800,
                    border: `1px solid ${alpha(GOLD, 0.3)}`,
                  }}
                >
                  {getInitials(s)}
                </Avatar>

                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography fontSize={15} fontWeight={700} color={TEXT_PRIMARY} noWrap
                      sx={{ cursor: "pointer", transition: "color 0.2s ease", "&:hover": { color: GOLD } }}
                      onClick={() => navigate(`/vendor-view/${s.id}`)}>
                      {name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 3, mt: 0.5, flexWrap: "wrap" }}>
                    {phone && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 12, color: "#D1D5DB" }} />
                        <Typography fontSize={12} color={TEXT_SECONDARY}>{phone}</Typography>
                      </Box>
                    )}
                    {email && (
                      <Typography fontSize={12} color={TEXT_SECONDARY}>{email}</Typography>
                    )}
                    {location && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <LocationOnIcon sx={{ fontSize: 12, color: "#D1D5DB" }} />
                        <Typography fontSize={12} color={TEXT_SECONDARY}>{location}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Actions */}
                <ActionButtons
                  onView={() => navigate(`/vendor-view/${s.id}`)}
                  onEdit={() => navigate(`/vendor-view/${s.id}`)}
                  onDelete={() => setDeleteTarget(s)}
                />
              </Box>
            );
          })}
        </Box>
      )}

      {/* Delete Popup */}
      {deleteTarget && (
        <DeletePopup open={!!deleteTarget} handleCloseDelete={() => setDeleteTarget(null)} onConfirm={handleDelete}
          title="Delete Supplier" content={`Are you sure you want to delete "${getName(deleteTarget)}"?`} />
      )}
    </Box>
  );
};

export default SupplierList;
